/**
 * Application configuration constants.
 *
 * Values from: implentation_briefs/tracker_gui/Frontend GUI Technical Briefing.md
 */

// =============================================================================
// API CONFIGURATION
// =============================================================================

export const API_CONFIG = {
  REST_BASE: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1',
  WS_BASE: import.meta.env.VITE_WS_BASE_URL || 'ws://localhost:8000/api/v1/ws/updates',
} as const;

// =============================================================================
// MAP CONFIGURATION
// =============================================================================

export const MAP_CONFIG = {
  // CartoCDN Voyager - clean, colorful style (free, no API key required)
  STYLE_URL:
    import.meta.env.VITE_MAP_STYLE_URL ||
    'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json',

  // CartoCDN Dark Matter - dark theme variant
  DARK_STYLE_URL:
    import.meta.env.VITE_MAP_DARK_STYLE_URL ||
    'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json',

  // Gothenburg center coordinates
  CENTER: [11.9746, 57.7089] as [number, number],

  // Zoom levels
  INITIAL_ZOOM: 12,
  MIN_ZOOM: 6,
  MAX_ZOOM: 18,

  // Bounds (optional - restrict to Gothenburg region)
  MAX_BOUNDS: [
    [10.9, 57.15], // Southwest (~35km expansion)
    [13.1, 58.35], // Northeast (~35km expansion)
  ] as [[number, number], [number, number]],
} as const;

// =============================================================================
// WEBSOCKET CONFIGURATION
// =============================================================================

export const WS_CONFIG = {
  // Delay before first reconnection attempt
  RECONNECT_DELAY_MS: 2000,
  // Maximum reconnection attempts before fallback
  MAX_RECONNECT_ATTEMPTS: 5,
  // Heartbeat interval (server sends every 30s)
  HEARTBEAT_INTERVAL_MS: 30_000,
  // Consider connection dead if no heartbeat for this long
  HEARTBEAT_TIMEOUT_MS: 45_000,
} as const;

// =============================================================================
// FRESHNESS CONFIGURATION
// Single source of truth: A vehicle is ACTIVE if V1 < 10s OR V4 < 20s
// =============================================================================

export const FRESHNESS = {
  /** V1 GPS position is fresh if age < 10 seconds */
  V1_ACTIVE_MS: 10_000,

  /** V4 estimated position is fresh if age < 20 seconds */
  V4_ACTIVE_MS: 20_000,

  /** Remove vehicle from store after no updates for this duration */
  CLEANUP_MS: 60_000,

  /** Cleanup interval for removing old vehicles */
  CLEANUP_INTERVAL_MS: 10_000,
} as const;

// =============================================================================
// TTL STATE CONFIGURATION (DEPRECATED - use FRESHNESS instead)
// =============================================================================

/** @deprecated Use FRESHNESS config instead */
export const TTL_CONFIG = {
  /** @deprecated Use FRESHNESS.CLEANUP_MS instead */
  STALE_MS: FRESHNESS.CLEANUP_MS,

  /** @deprecated Use FRESHNESS.CLEANUP_MS instead */
  DEAD_MS: FRESHNESS.CLEANUP_MS,

  /** @deprecated Use FRESHNESS.CLEANUP_INTERVAL_MS instead */
  CLEANUP_INTERVAL_MS: FRESHNESS.CLEANUP_INTERVAL_MS,
} as const;




// =============================================================================
// POLLER COLOR CONFIGURATION
// =============================================================================

export const POLLER_COLORS = {
  v4: '#3b82f6',     // blue-500
  v1da: '#a855f7',   // purple-500
} as const;

// =============================================================================
// V4 POSITION RENDERING CONFIGURATION
// =============================================================================

export const V4_POSITION_CONFIG = {
  // Master toggle for V4 position feature (estimated positions from V4 polling)
  ENABLED: true,

  // Opacity for V4 position markers
  OPACITY: 0.5,

  // Hide V4 position if within this distance of GPS position
  MERGE_THRESHOLD_METERS: 50,

  // Show deviation line between GPS and V4 position
  DEVIATION_LINE_ENABLED: true,

  // Distance at which deviation line turns red
  DEVIATION_WARNING_METERS: 200,
} as const;

// =============================================================================
// RENDERING CONFIGURATION
// =============================================================================

export const RENDER_CONFIG = {
  // Maximum vehicles to render for performance
  MAX_RENDERED_VEHICLES: 800,

  // Interpolation buffer for smooth live mode (offset from wall clock)
  INTERPOLATION_BUFFER_MS: 2000,

  // Expected polling interval from backend (used to set interpolation duration)
  // Should match the backend's polling_interval for smooth animation
  POLLING_INTERVAL_MS: Number(import.meta.env.VITE_POLLING_INTERVAL_MS) || 1000,

  // Animation duration between position updates
  // Set to match polling interval for smooth continuous movement
  get INTERPOLATION_DURATION_MS(): number {
    return this.POLLING_INTERVAL_MS;
  },

  // Smooth animation duration (poll interval + buffer for overlap trick)
  // This ensures smooth transitions by starting new animation before previous completes
  ANIMATION_DURATION_MS: 1500,

  // Target frame rate for smooth animation
  ANIMATION_FPS_TARGET: 60,

  // Opacity for estimated-only vehicles (no GPS)
  ESTIMATED_VEHICLE_OPACITY: 0.7,

  // Map opacity when a vehicle is selected
  MAP_DIM_ON_SELECT: 0.4,

  // Freshness dot configuration (admin-only)
  FRESHNESS_DOT: {
    RADIUS: 3,
    V4_TRANSLATE: [-5, 18] as readonly [number, number],
    V1DA_TRANSLATE: [5, 18] as readonly [number, number],
    COLORS: {
      fresh: '#22c55e',   // green-500
      warning: '#f97316', // orange-500
      stale: '#ef4444',   // red-500
      none: '#9ca3af',    // gray-400
    },
  },
} as const;

// =============================================================================
// LOD (LEVEL OF DETAIL) CONFIGURATION
// =============================================================================

export const LOD_CONFIG = {
  // Zoom thresholds for transport mode visibility
  ZOOM_THRESHOLDS: {
    // Priority 1: Always visible
    train: 0,
    ferry: 0,
    // Priority 2: Visible at zoom 11+
    bus: 8,
    tram: 9,
    // Priority 3: Visible at zoom 14+
    taxi: 10,
    teletaxi: 11,
  } as const,
} as const;

// =============================================================================
// ACCESSIBILITY CONFIGURATION
// =============================================================================

export const A11Y_CONFIG = {
  // Debounce ARIA live region updates
  UPDATE_DEBOUNCE_MS: 5000,
} as const;

// =============================================================================
// TANSTACK QUERY CONFIGURATION
// =============================================================================

export const QUERY_CONFIG = {
  // Default stale time for static data (5 minutes)
  STALE_TIME_MS: 5 * 60 * 1000,

  // Default cache time (10 minutes)
  GC_TIME_MS: 10 * 60 * 1000,

  // Retry configuration
  RETRY_COUNT: 3,
  RETRY_DELAY_MS: 1000,
} as const;

// =============================================================================
// VIEWPORT SUBSCRIPTION CONFIGURATION
// =============================================================================

export const VIEWPORT_CONFIG = {
  // Debounce viewport changes before sending subscription
  DEBOUNCE_MS: 200,
} as const;
